import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';

const toggleScopeItem = (arr, item) =>
  arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];

const PLANS = [
  { value: 'single', label: 'Single Location' },
  { value: 'multi', label: 'Multi Location' },
];
const CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Annual' },
];

export function CreateCouponDialog({ open, onClose, onSubmit, couponForm, setCouponForm, submitting }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Coupon</DialogTitle>
          <DialogDescription>Create a new discount coupon</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">Coupon Code</Label>
            <Input
              id="code"
              value={couponForm.code}
              onChange={(e) => setCouponForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="e.g., SAVE20"
              required
              data-testid="coupon-code-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="discount">Discount Percent</Label>
            <Input
              id="discount"
              type="number"
              min="1"
              max="100"
              value={couponForm.discount_percent}
              onChange={(e) => setCouponForm(prev => ({ ...prev, discount_percent: parseInt(e.target.value) }))}
              required
              data-testid="coupon-discount-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Applies to Plans <span className="text-red-500">*</span></Label>
            <div className="flex gap-4">
              {PLANS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={couponForm.applicable_plans.includes(value)}
                    onChange={() => setCouponForm(prev => ({
                      ...prev,
                      applicable_plans: toggleScopeItem(prev.applicable_plans, value),
                    }))}
                    data-testid={`coupon-plan-${value}`}
                    className="rounded border-slate-300"
                  />
                  {label}
                </label>
              ))}
            </div>
            {couponForm.applicable_plans.length === 0 && (
              <p className="text-xs text-red-500">Select at least one plan</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Applies to Billing Cycles <span className="text-red-500">*</span></Label>
            <div className="flex gap-4">
              {CYCLES.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={couponForm.applicable_cycles.includes(value)}
                    onChange={() => setCouponForm(prev => ({
                      ...prev,
                      applicable_cycles: toggleScopeItem(prev.applicable_cycles, value),
                    }))}
                    data-testid={`coupon-cycle-${value}`}
                    className="rounded border-slate-300"
                  />
                  {label}
                </label>
              ))}
            </div>
            {couponForm.applicable_cycles.length === 0 && (
              <p className="text-xs text-red-500">Select at least one billing cycle</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="max_uses">Max Uses (Optional)</Label>
            <Input
              id="max_uses"
              type="number"
              min="1"
              value={couponForm.max_uses}
              onChange={(e) => setCouponForm(prev => ({ ...prev, max_uses: e.target.value }))}
              placeholder="Leave empty for unlimited"
              data-testid="coupon-max-uses-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expires_at">Expiry Date (Optional)</Label>
            <Input
              id="expires_at"
              type="date"
              value={couponForm.expires_at}
              onChange={(e) => setCouponForm(prev => ({ ...prev, expires_at: e.target.value }))}
              data-testid="coupon-expiry-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={submitting} data-testid="coupon-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Coupon'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
